import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Bank, Plus, Pencil, Archive, Download, ArrowsLeftRight, ArrowDown, ArrowUp, CurrencyInr, Trash, Broom, Eraser, Upload, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Amount } from '../contexts/AmountVisibilityContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACC_TYPES = [
  { v: 'current', l: 'Current Account', icon: '🏦' },
  { v: 'savings', l: 'Savings Account', icon: '💰' },
  { v: 'personal', l: 'Personal Account', icon: '👤' },
  { v: 'cash', l: 'Cash', icon: '💵' },
  { v: 'upi', l: 'UPI', icon: '📱' },
  { v: 'card', l: 'Credit Card', icon: '💳' },
  { v: 'other', l: 'Other', icon: '📒' },
];

const emptyForm = () => ({
  id: '', nickname: '', account_type: 'current', bank_name: '', holder_name: '',
  account_no_last4: '', ifsc: '', upi_handle: '', currency: 'INR',
  opening_balance: 0, opening_balance_date: new Date().toISOString().slice(0, 10),
  is_active: true, color: '#1a2e4a', notes: '', company_id: '',
});

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function BankAccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [drillId, setDrillId] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // Manual entry form — supports MULTIPLE LINE ITEMS in one submit.
  // E.g. one Food expense = breakfast ₹20 + lunch ₹50 + dinner ₹70.
  const emptyLine = () => ({ amount: '', description: '', counterparty: '', reference: '' });
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    type: 'debit',
    category: '',        // optional shared label (e.g. "Food")
    lines: [emptyLine()],
  });
  // Transfer form
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({ from_account_id: '', to_account_id: '', amount: 0, date: new Date().toISOString().slice(0, 10), reference: '', notes: '' });

  // Bank statement import wizard
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null);   // { rows, summary }
  const [importSelected, setImportSelected] = useState({});   // { rowIdx: bool }
  const [importCommitting, setImportCommitting] = useState(false);

  const load = async () => {
    const [{ data: accs }, { data: cos }] = await Promise.all([
      axios.get(`${API_URL}/api/bank-accounts?include_archived=${showArchived}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/companies`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]);
    setAccounts(accs || []);
    setCompanies(cos || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const loadLedger = async (accId) => {
    if (!accId) return;
    setLedgerLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from_date', from);
      if (to) params.set('to_date', to);
      const { data } = await axios.get(`${API_URL}/api/bank-accounts/${accId}/ledger?${params.toString()}`, { withCredentials: true });
      setLedger(data);
    } catch (e) { toast.error('Failed to load ledger'); }
    setLedgerLoading(false);
  };
  useEffect(() => { if (drillId) loadLedger(drillId); /* eslint-disable-next-line */ }, [drillId, from, to]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startNew = () => { setEdit(false); setForm(emptyForm()); setOpen(true); };
  const startEdit = (a) => { setEdit(true); setForm({ ...emptyForm(), ...a }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.nickname.trim()) { toast.error('Account nickname is required'); return; }
    try {
      if (edit) await axios.put(`${API_URL}/api/bank-accounts/${form.id}`, form, { withCredentials: true });
      else await axios.post(`${API_URL}/api/bank-accounts`, form, { withCredentials: true });
      toast.success(edit ? 'Account updated' : 'Account created');
      setOpen(false); await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const archive = async (a) => {
    if (!window.confirm(`Archive "${a.nickname}"? Transactions will be preserved for audit.`)) return;
    try {
      await axios.delete(`${API_URL}/api/bank-accounts/${a.id}`, { withCredentials: true });
      toast.success('Account archived');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const exportCsv = (a) => {
    const params = new URLSearchParams();
    if (from) params.set('from_date', from);
    if (to) params.set('to_date', to);
    window.open(`${API_URL}/api/bank-accounts/${a.id}/export.csv?${params.toString()}`, '_blank');
  };

  const submitManual = async (e) => {
    e.preventDefault();
    const valid = manualForm.lines.filter((l) => Number(l.amount) > 0);
    if (valid.length === 0) { toast.error('Enter at least one line with amount > 0'); return; }
    try {
      // POST each line in sequence; backend treats each as an independent ledger entry.
      // Description prefixed with shared category (e.g. "Food — Breakfast at Tea Stop").
      const cat = (manualForm.category || '').trim();
      for (const line of valid) {
        const desc = cat
          ? (line.description ? `${cat} — ${line.description}` : cat)
          : (line.description || '');
        await axios.post(`${API_URL}/api/bank-accounts/${drillId}/transactions`, {
          account_id: drillId,
          source_type: 'manual',
          date: manualForm.date,
          type: manualForm.type,
          amount: Number(line.amount),
          description: desc,
          counterparty: line.counterparty || '',
          reference: line.reference || '',
        }, { withCredentials: true });
      }
      toast.success(`${valid.length} ${manualForm.type === 'credit' ? 'credit' : 'debit'} ${valid.length === 1 ? 'entry' : 'entries'} added`);
      setManualOpen(false);
      setManualForm({ date: new Date().toISOString().slice(0, 10), type: 'debit', category: '', lines: [emptyLine()] });
      await loadLedger(drillId); await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const addLine = () => setManualForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (idx) => setManualForm((f) => ({ ...f, lines: f.lines.length === 1 ? [emptyLine()] : f.lines.filter((_, i) => i !== idx) }));
  const updateLine = (idx, key, val) => setManualForm((f) => ({
    ...f,
    lines: f.lines.map((l, i) => i === idx ? { ...l, [key]: val } : l),
  }));
  const manualLinesTotal = manualForm.lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!transferForm.from_account_id || !transferForm.to_account_id) { toast.error('Pick both accounts'); return; }
    if (transferForm.from_account_id === transferForm.to_account_id) { toast.error('Source and destination must differ'); return; }
    try {
      await axios.post(`${API_URL}/api/bank-accounts/transfer`, { ...transferForm, amount: Number(transferForm.amount) }, { withCredentials: true });
      toast.success('Transfer recorded');
      setTransferOpen(false);
      setTransferForm({ from_account_id: '', to_account_id: '', amount: 0, date: new Date().toISOString().slice(0, 10), reference: '', notes: '' });
      await load();
      if (drillId) await loadLedger(drillId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openImportDialog = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportSelected({});
    setImportOpen(true);
  };

  const previewImport = async () => {
    if (!importFile) { toast.error('Please choose a CSV or Excel file first'); return; }
    setImportLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const { data } = await axios.post(
        `${API_URL}/api/bank-accounts/${drillId}/import-statement/preview`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      // Enrich each row with editable fields (category_id + mark_invoice_paid)
      const enriched = (data.rows || []).map((r) => ({
        ...r,
        category_id: r.suggested_category_id || '',
        mark_invoice_paid: !!r.matched_invoice_id,
      }));
      setImportPreview({ ...data, rows: enriched });
      const sel = {};
      enriched.forEach((r, idx) => { sel[idx] = !r.is_duplicate; });
      setImportSelected(sel);
      toast.success(
        `Parsed ${data.summary.total} rows — ${data.summary.new} new, ${data.summary.auto_categorised} auto-categorised, ${data.summary.invoice_matched} invoice matches`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse the file');
    }
    setImportLoading(false);
  };

  const setImportRowField = (idx, patch) => {
    setImportPreview((p) => {
      if (!p) return p;
      const rows = p.rows.slice();
      rows[idx] = { ...rows[idx], ...patch };
      return { ...p, rows };
    });
  };

  const commitImport = async () => {
    if (!importPreview) return;
    const rowsToImport = importPreview.rows.filter((_, idx) => importSelected[idx]);
    if (rowsToImport.length === 0) { toast.error('Tick at least one row to import'); return; }
    setImportCommitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/bank-accounts/${drillId}/import-statement/commit`,
        { rows: rowsToImport.map(({ is_duplicate: _d, suggested_category_id: _sc, suggested_category_name: _scn, matched_invoice_outstanding: _mo, matched_invoice_client: _mc, ...r }) => r) },
        { withCredentials: true }
      );
      const bits = [`${data.imported} transactions imported`];
      if (data.invoices_marked_paid > 0) bits.push(`${data.invoices_marked_paid} invoice${data.invoices_marked_paid === 1 ? '' : 's'} marked paid`);
      toast.success(bits.join(' · '));
      setImportOpen(false);
      setImportFile(null);
      setImportPreview(null);
      setImportSelected({});
      await loadLedger(drillId);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setImportCommitting(false);
  };

  const toggleImportRow = (idx) => setImportSelected((s) => ({ ...s, [idx]: !s[idx] }));
  const toggleAllImportRows = (checked) => {
    if (!importPreview) return;
    const sel = {};
    importPreview.rows.forEach((r, idx) => { sel[idx] = checked && !r.is_duplicate; });
    setImportSelected(sel);
  };

  const deleteTxn = async (txnId) => {
    if (!window.confirm('Delete this ledger entry? This will desync any linked expense/invoice if applicable.')) return;
    try {
      await axios.delete(`${API_URL}/api/bank-accounts/transactions/${txnId}`, { withCredentials: true });
      toast.success('Entry deleted');
      await loadLedger(drillId); await load();
    } catch (err) { toast.error('Failed'); }
  };

  const resetOneAccount = async (a) => {
    const phrase = `RESET ${a.nickname}`;
    const ans = window.prompt(
      `⚠️ Fully reset "${a.nickname}" to ZERO?\n\n` +
      `• Every ledger transaction will be DELETED.\n` +
      `• Opening balance will be set to ₹0 (currently ₹${Number(a.opening || 0).toLocaleString('en-IN')}).\n` +
      `• Linked invoices & expenses are kept, only their "paid via this account" linkage is cleared.\n\n` +
      `Type:  ${phrase}\nto confirm.`
    );
    if (ans !== phrase) { if (ans !== null) toast.error('Phrase did not match — cancelled'); return; }
    try {
      const r = await axios.post(`${API_URL}/api/bank-accounts/${a.id}/reset`, {}, { withCredentials: true });
      toast.success(r.data?.message || 'Reset done');
      if (drillId === a.id) await loadLedger(a.id);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
  };

  const resetAllCashBook = async () => {
    const ans = window.prompt(
      `🛑 DESTRUCTIVE — wipe Cash Book across ALL ${accounts.length} accounts to ZERO?\n\n` +
      `• Every ledger transaction will be DELETED.\n` +
      `• EVERY account's opening balance will be set to ₹0.\n` +
      `• Invoice receipts & expense payment records are kept, but their bank-account linkage is cleared.\n\n` +
      `Type:  DELETE-ALL\nto confirm.`
    );
    if (ans !== 'DELETE-ALL') { if (ans !== null) toast.error('Phrase did not match — cancelled'); return; }
    try {
      const r = await axios.post(`${API_URL}/api/cash-book/reset-all?confirm=DELETE-ALL`, {}, { withCredentials: true });
      toast.success(r.data?.message || 'Cash Book cleared');
      setDrillId(null);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
  };

  const totalBalance = useMemo(() => accounts.reduce((a, x) => a + (x.balance || 0), 0), [accounts]);

  if (drillId && ledger) {
    const a = ledger.account;
    const s = ledger.summary;
    return (
      <div className="space-y-4" data-testid="account-ledger-pane">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => { setDrillId(null); setLedger(null); }} data-testid="ledger-back">← Back to Accounts</Button>
          <h1 className="text-2xl font-bold flex-1" style={{ color: '#1a2e4a' }}>{a.nickname}</h1>
          <Button size="sm" variant="outline" onClick={() => { setManualForm({ date: new Date().toISOString().slice(0, 10), type: 'credit', category: '', lines: [emptyLine()] }); setManualOpen(true); }} data-testid="ledger-add-credit"><ArrowUp size={14} className="mr-1 text-emerald-600" />Add Credit</Button>
          <Button size="sm" variant="outline" onClick={() => { setManualForm({ date: new Date().toISOString().slice(0, 10), type: 'debit', category: '', lines: [emptyLine()] }); setManualOpen(true); }} data-testid="ledger-add-debit"><ArrowDown size={14} className="mr-1 text-rose-600" />Add Debit</Button>
          <Button size="sm" variant="outline" onClick={openImportDialog} data-testid="ledger-import-statement" title="Import bank statement (CSV or Excel)"><Upload size={14} className="mr-1 text-indigo-600" />Import Statement</Button>
          <Button size="sm" variant="outline" onClick={() => exportCsv(a)} data-testid="ledger-export"><Download size={14} className="mr-1" />Export CSV</Button>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <Card className="p-3 bg-slate-50"><div className="text-[10px] uppercase tracking-wider text-gray-500">Opening Balance</div><div className="text-lg font-bold" style={{ color: '#1a2e4a' }}><Amount value={s.opening} /></div></Card>
          <Card className="p-3 bg-emerald-50"><div className="text-[10px] uppercase tracking-wider text-emerald-700">Total Credits</div><div className="text-lg font-bold text-emerald-700"><Amount value={s.credits} /></div></Card>
          <Card className="p-3 bg-rose-50"><div className="text-[10px] uppercase tracking-wider text-rose-700">Total Debits</div><div className="text-lg font-bold text-rose-700"><Amount value={s.debits} /></div></Card>
          <Card className="p-3" style={{ background: a.color || '#1a2e4a', color: 'white' }}><div className="text-[10px] uppercase tracking-wider opacity-80">Current Balance</div><div className="text-2xl font-black"><Amount value={s.balance} /></div></Card>
        </div>

        <Card className="p-3 flex items-center gap-2 flex-wrap">
          <Label className="text-xs">From</Label>
          <Input type="date" className="w-40" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ledger-from" />
          <Label className="text-xs">To</Label>
          <Input type="date" className="w-40" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ledger-to" />
          {(from || to) && <Button size="sm" variant="ghost" onClick={() => { setFrom(''); setTo(''); }}>Clear</Button>}
        </Card>

        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead style={{ background: '#f4f4f5' }}>
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Date</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Description</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Source</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Reference</th>
                <th className="text-right px-3 py-2 font-semibold text-xs uppercase text-rose-700">Debit</th>
                <th className="text-right px-3 py-2 font-semibold text-xs uppercase text-emerald-700">Credit</th>
                <th className="text-right px-3 py-2 font-semibold text-xs uppercase">Running Balance</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {ledgerLoading ? <tr><td colSpan={8} className="text-center py-8 text-gray-500">Loading…</td></tr>
                : ledger.transactions.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-gray-500" data-testid="ledger-empty">No transactions in this window.</td></tr>
                : ledger.transactions.map((t) => (
                  <tr key={t.id} className={`border-t ${t.is_opening ? 'bg-blue-50/30' : 'hover:bg-gray-50'}`} data-testid={`ledger-row-${t.id}`}>
                    <td className="px-3 py-2 text-xs">{t.date}</td>
                    <td className="px-3 py-2">{t.description || '—'}{t.counterparty && <span className="text-[10px] text-gray-500 block">{t.counterparty}</span>}</td>
                    <td className="px-3 py-2"><span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase" style={{ background: t.source_type === 'expense' ? '#fee2e2' : t.source_type === 'invoice_payment' ? '#d1fae5' : t.source_type === 'transfer' ? '#dbeafe' : t.source_type === 'opening' ? '#f1f5f9' : '#fef3c7', color: '#1a2e4a' }}>{t.source_type}</span></td>
                    <td className="px-3 py-2 text-xs text-gray-600 font-mono">{t.reference || '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold text-rose-700 tabular-nums whitespace-nowrap">{t.type === 'debit' && !t.is_opening ? inr(t.amount) : ''}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-700 tabular-nums whitespace-nowrap">{t.type === 'credit' && !t.is_opening ? inr(t.amount) : ''}</td>
                    <td className="px-3 py-2 text-right font-bold tabular-nums whitespace-nowrap" style={{ color: t.running_balance < 0 ? '#dc2626' : '#1a2e4a' }}>{inr(t.running_balance)}</td>
                    <td className="px-3 py-2 text-center">
                      {!t.is_opening && t.source_type === 'manual' && (
                        <button onClick={() => deleteTxn(t.id)} className="text-rose-600 hover:bg-rose-50 p-1 rounded" title="Delete"><Trash size={12} /></button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Card>

        {/* Manual entry dialog — supports multiple line items in one go */}
        <Dialog open={manualOpen} onOpenChange={setManualOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className={manualForm.type === 'credit' ? 'text-emerald-600' : 'text-rose-600'}>
                  {manualForm.type === 'credit' ? <ArrowUp size={18} weight="bold" /> : <ArrowDown size={18} weight="bold" />}
                </span>
                <span>{manualForm.type === 'credit' ? 'Add Credit' : 'Add Debit'} — {a.nickname}</span>
              </DialogTitle>
              <div className="text-[11px] text-gray-500 mt-1">Add one entry, or split into multiple lines (e.g. breakfast ₹20 + lunch ₹50 + dinner ₹70 as one Food group).</div>
            </DialogHeader>
            <form onSubmit={submitManual} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-bold text-gray-500">Date</Label>
                  <Input type="date" value={manualForm.date} onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-bold text-gray-500">
                    Group / Category <span className="text-gray-400 normal-case">(optional, prefixed to each line)</span>
                  </Label>
                  <Input
                    value={manualForm.category}
                    onChange={(e) => setManualForm({ ...manualForm, category: e.target.value })}
                    placeholder="e.g. Food, Travel, Utilities"
                    data-testid="manual-category"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-[11px] uppercase tracking-wider font-bold text-gray-500">
                    Line Items ({manualForm.lines.length})
                  </Label>
                  <div className="text-xs">
                    <span className="text-gray-400">Total:&nbsp;</span>
                    <span className={`font-bold tabular-nums ${manualForm.type === 'credit' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      ₹{manualLinesTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  {manualForm.lines.map((line, idx) => (
                    <div key={idx} className="border rounded-lg p-2.5 bg-slate-50/50" data-testid={`manual-line-${idx}`}>
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-3">
                          <Label className="text-[9px] uppercase font-bold text-gray-500">Amount (₹)</Label>
                          <Input type="number" step="0.01" value={line.amount}
                            onChange={(e) => updateLine(idx, 'amount', e.target.value)}
                            placeholder="0.00" data-testid={`manual-amount-${idx}`} />
                        </div>
                        <div className="col-span-5">
                          <Label className="text-[9px] uppercase font-bold text-gray-500">Description</Label>
                          <Input value={line.description}
                            onChange={(e) => updateLine(idx, 'description', e.target.value)}
                            placeholder={manualForm.category ? `${manualForm.category} — what was bought` : 'e.g. Bank charges'}
                            data-testid={`manual-desc-${idx}`} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[9px] uppercase font-bold text-gray-500">Counterparty</Label>
                          <Input value={line.counterparty}
                            onChange={(e) => updateLine(idx, 'counterparty', e.target.value)}
                            placeholder="optional" />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-[9px] uppercase font-bold text-gray-500">Ref</Label>
                          <Input value={line.reference}
                            onChange={(e) => updateLine(idx, 'reference', e.target.value)}
                            placeholder="UTR" />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Button type="button" size="sm" variant="outline" onClick={() => removeLine(idx)}
                            className="text-rose-600 border-rose-200 hover:bg-rose-50 h-9 w-9 p-0"
                            disabled={manualForm.lines.length === 1}
                            title={manualForm.lines.length === 1 ? 'At least one line required' : 'Remove this line'}
                            data-testid={`manual-remove-${idx}`}>
                            <Trash size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" size="sm" variant="outline" onClick={addLine} className="mt-2 w-full border-dashed" data-testid="manual-add-line">
                  <Plus size={12} className="mr-1" /> Add another line
                </Button>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button type="button" variant="outline" onClick={() => setManualOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="manual-submit" className="text-white"
                  style={{ background: manualForm.type === 'credit' ? '#15803d' : '#b91c1c' }}>
                  Save {manualForm.lines.filter(l => Number(l.amount) > 0).length || ''} {manualForm.type === 'credit' ? 'Credit' : 'Debit'}{(manualForm.lines.filter(l => Number(l.amount) > 0).length > 1) ? 's' : ''}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bank Statement Import Wizard */}
        <Dialog open={importOpen} onOpenChange={(v) => { if (!importCommitting) setImportOpen(v); }}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload size={20} className="text-indigo-600" weight="bold" />
                Import Bank Statement — {a.nickname}
              </DialogTitle>
              <div className="text-[11px] text-gray-500 mt-1">
                Upload a CSV or Excel export from your bank. We auto-detect columns and flag any transactions that already exist so you never double-post.
              </div>
            </DialogHeader>

            {!importPreview && (
              <div className="space-y-4 py-4">
                <div className="border-2 border-dashed border-indigo-200 bg-indigo-50/40 rounded-lg p-6 text-center">
                  <Upload size={32} className="mx-auto text-indigo-500 mb-2" />
                  <Label className="text-sm font-semibold text-gray-700">Choose bank statement file</Label>
                  <Input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="mt-3 max-w-md mx-auto"
                    data-testid="import-file-input"
                  />
                  {importFile && (
                    <div className="mt-2 text-xs text-gray-600">
                      Selected: <span className="font-mono font-semibold">{importFile.name}</span>
                      {' · '}{Math.round(importFile.size / 1024)} KB
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-gray-500 bg-slate-50 rounded p-3 space-y-1">
                  <div className="font-semibold text-gray-700">Supported formats</div>
                  <div>· Kotak Mahindra (Amount + Dr/Cr columns)</div>
                  <div>· HDFC / ICICI / SBI / Axis (separate Withdrawal & Deposit columns)</div>
                  <div>· Any CSV/Excel with columns: Date, Description/Narration, Amount (or Withdrawal + Deposit)</div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button type="button" variant="outline" onClick={() => setImportOpen(false)} data-testid="import-cancel-btn">Cancel</Button>
                  <Button
                    type="button"
                    onClick={previewImport}
                    disabled={!importFile || importLoading}
                    data-testid="import-parse-btn"
                    className="text-white"
                    style={{ background: '#4f46e5' }}
                  >
                    {importLoading ? 'Parsing…' : 'Parse File →'}
                  </Button>
                </div>
              </div>
            )}

            {importPreview && (
              <>
                {/* Summary strip */}
                <div className="grid grid-cols-6 gap-2 mb-3">
                  <Card className="p-2.5 bg-slate-50">
                    <div className="text-[9px] uppercase tracking-wider text-gray-500">Total rows</div>
                    <div className="text-lg font-bold text-gray-800" data-testid="import-total">{importPreview.summary.total}</div>
                  </Card>
                  <Card className="p-2.5 bg-emerald-50">
                    <div className="text-[9px] uppercase tracking-wider text-emerald-700">New</div>
                    <div className="text-lg font-bold text-emerald-700" data-testid="import-new">{importPreview.summary.new}</div>
                  </Card>
                  <Card className="p-2.5 bg-amber-50">
                    <div className="text-[9px] uppercase tracking-wider text-amber-700">Duplicates</div>
                    <div className="text-lg font-bold text-amber-700" data-testid="import-duplicates">{importPreview.summary.duplicates}</div>
                  </Card>
                  <Card className="p-2.5 bg-indigo-50">
                    <div className="text-[9px] uppercase tracking-wider text-indigo-700">Auto-Categorised</div>
                    <div className="text-lg font-bold text-indigo-700" data-testid="import-categorised">{importPreview.summary.auto_categorised || 0}</div>
                  </Card>
                  <Card className="p-2.5 bg-teal-50">
                    <div className="text-[9px] uppercase tracking-wider text-teal-700">Invoice Matches</div>
                    <div className="text-lg font-bold text-teal-700" data-testid="import-matches">{importPreview.summary.invoice_matched || 0}</div>
                  </Card>
                  <Card className="p-2.5 bg-slate-50">
                    <div className="text-[9px] uppercase tracking-wider text-slate-600">Cr / Dr Σ</div>
                    <div className="text-[11px] font-bold text-emerald-700 tabular-nums leading-tight">+ {inr(importPreview.summary.credit_total)}</div>
                    <div className="text-[11px] font-bold text-rose-700 tabular-nums leading-tight">− {inr(importPreview.summary.debit_total)}</div>
                  </Card>
                </div>

                {/* Table */}
                <div className="flex-1 overflow-auto border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0 z-10">
                      <tr>
                        <th className="px-2 py-2 w-8">
                          <input
                            type="checkbox"
                            onChange={(e) => toggleAllImportRows(e.target.checked)}
                            checked={
                              importPreview.rows.length > 0 &&
                              importPreview.rows.every((r, i) => r.is_duplicate || importSelected[i])
                            }
                            data-testid="import-select-all"
                            title="Select / deselect all non-duplicates"
                          />
                        </th>
                        <th className="px-2 py-2 text-left uppercase font-semibold">Date</th>
                        <th className="px-2 py-2 text-left uppercase font-semibold">Description</th>
                        <th className="px-2 py-2 text-right uppercase font-semibold text-rose-700">Debit</th>
                        <th className="px-2 py-2 text-right uppercase font-semibold text-emerald-700">Credit</th>
                        <th className="px-2 py-2 text-left uppercase font-semibold w-[220px]">Category / Match</th>
                        <th className="px-2 py-2 text-center uppercase font-semibold w-[90px]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.rows.map((r, idx) => (
                        <tr
                          key={idx}
                          className={`border-t ${r.is_duplicate ? 'bg-amber-50/40 text-gray-500' : 'hover:bg-slate-50'}`}
                          data-testid={`import-row-${idx}`}
                        >
                          <td className="px-2 py-1.5 text-center align-top">
                            <input
                              type="checkbox"
                              checked={!!importSelected[idx]}
                              onChange={() => toggleImportRow(idx)}
                              disabled={r.is_duplicate}
                              data-testid={`import-row-check-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1.5 font-mono align-top">{r.date}</td>
                          <td className="px-2 py-1.5 align-top max-w-[280px]">
                            <div className="truncate" title={r.description}>{r.description || '—'}</div>
                            {r.reference && <div className="font-mono text-[9px] text-gray-500 truncate" title={r.reference}>{r.reference}</div>}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-rose-700 font-semibold align-top">{r.type === 'debit' ? inr(r.amount) : ''}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700 font-semibold align-top">{r.type === 'credit' ? inr(r.amount) : ''}</td>
                          <td className="px-2 py-1.5 align-top">
                            {r.type === 'debit' && (
                              <select
                                value={r.category_id || ''}
                                onChange={(e) => setImportRowField(idx, { category_id: e.target.value })}
                                disabled={r.is_duplicate}
                                data-testid={`import-row-category-${idx}`}
                                className="w-full text-[11px] border rounded px-1.5 py-1 bg-white disabled:bg-slate-100"
                              >
                                <option value="">— Uncategorised —</option>
                                {(importPreview.categories || []).map((c) => (
                                  <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                              </select>
                            )}
                            {r.type === 'credit' && r.matched_invoice_id && (
                              <div className="flex flex-col gap-1">
                                <div className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded self-start">
                                  <CheckCircle size={10} weight="bold" />
                                  {r.matched_invoice_number}
                                  {r.matched_invoice_client && <span className="font-normal text-teal-600">· {r.matched_invoice_client}</span>}
                                </div>
                                <label className="inline-flex items-center gap-1 text-[10px] text-gray-600 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={!!r.mark_invoice_paid}
                                    onChange={(e) => setImportRowField(idx, { mark_invoice_paid: e.target.checked })}
                                    disabled={r.is_duplicate}
                                    data-testid={`import-row-mark-paid-${idx}`}
                                  />
                                  Mark invoice as paid on import
                                </label>
                              </div>
                            )}
                            {r.type === 'credit' && !r.matched_invoice_id && (
                              <span className="text-[10px] text-gray-400 italic">no invoice match</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-center align-top">
                            {r.is_duplicate ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                <WarningCircle size={10} weight="bold" />Duplicate
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                                <CheckCircle size={10} weight="bold" />New
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between gap-2 pt-3 border-t">
                  <div className="text-xs text-gray-600">
                    <span className="font-bold" data-testid="import-selected-count">
                      {Object.values(importSelected).filter(Boolean).length}
                    </span>
                    {' '}of {importPreview.summary.total} rows selected for import
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => { setImportPreview(null); setImportSelected({}); }} data-testid="import-back-btn">← Choose different file</Button>
                    <Button type="button" variant="outline" onClick={() => setImportOpen(false)} disabled={importCommitting}>Cancel</Button>
                    <Button
                      type="button"
                      onClick={commitImport}
                      disabled={importCommitting || Object.values(importSelected).filter(Boolean).length === 0}
                      className="text-white"
                      style={{ background: '#15803d' }}
                      data-testid="import-commit-btn"
                    >
                      {importCommitting ? 'Importing…' : `Import ${Object.values(importSelected).filter(Boolean).length} Transaction${Object.values(importSelected).filter(Boolean).length === 1 ? '' : 's'}`}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#1a2e4a' }}>Cash Book</h1>
          <p className="text-sm text-gray-500">Live balance across all your accounts — auditor-ready.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived">
            {showArchived ? '👁 Hide archived' : '📂 Show archived'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setTransferOpen(true)} data-testid="transfer-btn"><ArrowsLeftRight size={14} className="mr-1" />Transfer</Button>
          <Button size="sm" variant="outline" className="text-rose-700 border-rose-300 hover:bg-rose-50" onClick={resetAllCashBook} disabled={accounts.length === 0} data-testid="reset-cashbook-btn" title="Wipe every ledger entry across all accounts">
            <Eraser size={14} className="mr-1" />Reset Cash Book
          </Button>
          <Button onClick={startNew} data-testid="new-account-btn"><Plus size={14} className="mr-1" />Add Account</Button>
        </div>
      </div>

      {/* Summary tile */}
      <Card className="p-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1a2e4a 100%)', color: 'white' }}>
        <div>
          <div className="text-xs uppercase tracking-wider opacity-70">Total Liquidity (across {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'})</div>
          <div className="text-3xl font-black mt-1 tabular-nums"><Amount value={totalBalance} /></div>
        </div>
        <Bank size={48} weight="duotone" className="opacity-50" />
      </Card>

      {/* Account cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {accounts.length === 0 ? (
          <Card className="col-span-full p-8 text-center text-gray-500">No accounts yet. Click "Add Account" to start tracking.</Card>
        ) : accounts.map((a) => {
          const typeInfo = ACC_TYPES.find((t) => t.v === a.account_type) || ACC_TYPES[6];
          return (
            <Card key={a.id} className={`p-0 overflow-hidden ${!a.is_active ? 'opacity-60' : ''}`} data-testid={`account-card-${a.id}`}>
              <div className="p-4 text-white" style={{ background: a.color || '#1a2e4a' }}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest opacity-70">{typeInfo.icon} {typeInfo.l}</div>
                    <div className="text-lg font-bold mt-0.5 truncate">{a.nickname}</div>
                    <div className="text-[11px] opacity-70 mt-1">
                      {a.bank_name && <span>{a.bank_name}</span>}
                      {a.account_no_last4 && <span> · ****{a.account_no_last4}</span>}
                      {a.upi_handle && <span>{a.upi_handle}</span>}
                    </div>
                  </div>
                  {!a.is_active && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 bg-white/20 rounded">Archived</span>}
                </div>
                <div className="mt-3 pt-3 border-t border-white/20">
                  <div className="text-[10px] uppercase tracking-wider opacity-70">Current Balance</div>
                  <div className="text-2xl font-black tabular-nums" data-testid={`account-balance-${a.id}`}><Amount value={a.balance} /></div>
                  <div className="text-[10px] opacity-60 mt-0.5 tabular-nums">Opening: <Amount value={a.opening} /> · {a.txn_count} txn</div>
                </div>
              </div>
              <div className="p-2 flex items-center justify-between gap-1 bg-white">
                <Button size="sm" variant="ghost" className="flex-1 text-xs" onClick={() => setDrillId(a.id)} data-testid={`account-ledger-${a.id}`}>📒 View Ledger</Button>
                <Button size="sm" variant="ghost" className="text-xs" onClick={() => exportCsv(a)} title="Export CSV" data-testid={`account-export-${a.id}`}><Download size={14} /></Button>
                <Button size="sm" variant="ghost" className="text-xs" onClick={() => startEdit(a)} title="Edit" data-testid={`account-edit-${a.id}`}><Pencil size={14} /></Button>
                <Button size="sm" variant="ghost" className="text-xs text-rose-600" onClick={() => resetOneAccount(a)} title="Reset ledger (wipe all transactions)" data-testid={`account-reset-${a.id}`}><Broom size={14} /></Button>
                {a.is_active && <Button size="sm" variant="ghost" className="text-xs text-rose-600" onClick={() => archive(a)} title="Archive" data-testid={`account-archive-${a.id}`}><Archive size={14} /></Button>}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Create / edit account dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{edit ? 'Edit Account' : 'Add Bank Account'}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nickname *</Label>
                <Input value={form.nickname} onChange={(e) => set('nickname', e.target.value)} placeholder='e.g. "Dextra Tech — HDFC Current"' required data-testid="account-nickname" />
              </div>
              <div>
                <Label>Account Type *</Label>
                <Select value={form.account_type} onValueChange={(v) => set('account_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ACC_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.icon} {t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Holder / Company Name</Label><Input value={form.holder_name} onChange={(e) => set('holder_name', e.target.value)} placeholder="Dextra Technologies / Personal / …" /></div>
              <div>
                <Label>Link to Company (optional)</Label>
                <Select value={form.company_id || '__none'} onValueChange={(v) => set('company_id', v === '__none' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="— None —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— None / Personal —</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={(e) => set('bank_name', e.target.value)} placeholder="HDFC / Kotak / SBI…" /></div>
              <div><Label>A/C Last 4</Label><Input value={form.account_no_last4} onChange={(e) => set('account_no_last4', e.target.value)} maxLength="4" placeholder="0022" /></div>
              <div><Label>IFSC</Label><Input value={form.ifsc} onChange={(e) => set('ifsc', e.target.value)} placeholder="KKBK0008483" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>UPI Handle</Label><Input value={form.upi_handle} onChange={(e) => set('upi_handle', e.target.value)} placeholder="dextra@okhdfcbank" /></div>
              <div><Label>Currency</Label><Select value={form.currency} onValueChange={(v) => set('currency', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="INR">INR ₹</SelectItem><SelectItem value="USD">USD $</SelectItem><SelectItem value="GBP">GBP £</SelectItem></SelectContent></Select></div>
              <div><Label>Card Color</Label><Input type="color" value={form.color || '#1a2e4a'} onChange={(e) => set('color', e.target.value)} className="h-9" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Opening Balance (₹)</Label><Input type="number" step="0.01" value={form.opening_balance} onChange={(e) => set('opening_balance', Number(e.target.value))} data-testid="account-opening-balance" /></div>
              <div><Label>Opening Balance Date</Label><Input type="date" value={form.opening_balance_date} onChange={(e) => set('opening_balance_date', e.target.value)} data-testid="account-opening-date" /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Optional internal note" /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-account-btn">{edit ? 'Update' : 'Create Account'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>↔ Transfer Between Accounts</DialogTitle></DialogHeader>
          <form onSubmit={submitTransfer} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From</Label>
                <Select value={transferForm.from_account_id} onValueChange={(v) => setTransferForm({ ...transferForm, from_account_id: v })}>
                  <SelectTrigger data-testid="transfer-from"><SelectValue placeholder="Source" /></SelectTrigger>
                  <SelectContent>{accounts.filter((a) => a.is_active).map((a) => <SelectItem key={a.id} value={a.id}>{a.nickname} — {inr(a.balance)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>To</Label>
                <Select value={transferForm.to_account_id} onValueChange={(v) => setTransferForm({ ...transferForm, to_account_id: v })}>
                  <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Destination" /></SelectTrigger>
                  <SelectContent>{accounts.filter((a) => a.is_active).map((a) => <SelectItem key={a.id} value={a.id}>{a.nickname} — {inr(a.balance)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (₹)</Label><Input type="number" step="0.01" value={transferForm.amount} onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })} data-testid="transfer-amount" /></div>
              <div><Label>Date</Label><Input type="date" value={transferForm.date} onChange={(e) => setTransferForm({ ...transferForm, date: e.target.value })} /></div>
            </div>
            <div><Label>Reference</Label><Input value={transferForm.reference} onChange={(e) => setTransferForm({ ...transferForm, reference: e.target.value })} placeholder="UTR / cheque no" /></div>
            <div><Label>Notes</Label><Input value={transferForm.notes} onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="transfer-submit">Record Transfer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
